import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type {
  MediaRef,
  ProductVariant,
  SeoMeta,
  SpecGroup,
  TranslatableString,
} from '@geoic/shared';
import { Card, CardBody, CardHeader } from '@/components/ui/Card.js';
import { Checkbox, Input, Select } from '@/components/ui/Field.js';
import { Tabs } from '@/components/ui/Tabs.js';
import { TranslatableInput } from '@/components/form/TranslatableInput.js';
import { RichTextInput } from '@/components/form/RichTextInput.js';
import { MediaPicker } from '@/components/media/MediaPicker.js';
import { MediaGalleryPicker } from '@/components/media/MediaGalleryPicker.js';
import { queryKeys } from '@/lib/query-keys.js';
import { useAuth } from '@/features/auth/auth-context.js';
import { ContentFormLayout } from '@/features/content/ContentFormLayout.js';
import { DraftBanner } from '@/features/content/DraftBanner.js';
import { SeoPanel, emptySeo } from '@/features/content/SeoPanel.js';
import { useContentForm } from '@/features/content/useContentForm.js';
import {
  EnumSelect,
  MultiSelect,
  Repeater,
  emptyTranslatable,
} from '@/features/blocks/BlockFields.js';
import { SpecBuilder } from './SpecBuilder.js';
import { VariantsEditor } from './VariantsEditor.js';
import { brandsApi, categoriesApi, productsApi, type ProductRecord } from './shop-api.js';

type ProductDocument = { title: TranslatableString; file: MediaRef; order: number };

type ProductForm = {
  title: TranslatableString;
  slug: string;
  shortDescription: TranslatableString;
  description: TranslatableString;
  brandId: string;
  categoryIds: string[];
  coverImage: MediaRef | null;
  gallery: MediaRef[];
  video: { source: 'youtube' | 'upload'; url: string; poster: MediaRef | null } | null;
  specGroups: SpecGroup[];
  documents: ProductDocument[];
  variants: ProductVariant[];
  isFeatured: boolean;
  seo: SeoMeta;
};

const TABS = [
  ['main', 'Asosiy'],
  ['specs', 'Xarakteristikalar'],
  ['variants', 'Komplektatsiyalar'],
  ['media', 'Galereya'],
  ['documents', 'Hujjatlar'],
  ['seo', 'SEO'],
] as const;

type Tab = (typeof TABS)[number][0];

/**
 * Mahsulot kartochkasi — 6 yorliq (TZ 7.1).
 *
 * Bitta uzun formaga sig'maydi: xarakteristikalar 40 qatorgacha,
 * komplektatsiyalar 20 tagacha bo'lishi mumkin. Yorliqlarsiz xodim
 * kerakli maydongacha yarim daqiqa aylantirardi.
 */
export function ProductEditPage() {
  const { id = 'new' } = useParams();
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('main');

  const listParams = { limit: 100, sort: 'title' as const };

  const categories = useQuery({
    queryKey: queryKeys.content.list('categories', { ...listParams, purpose: 'options' }),
    queryFn: ({ signal }) => categoriesApi.list(listParams, signal),
    staleTime: 5 * 60 * 1000,
  });

  const brands = useQuery({
    queryKey: queryKeys.content.list('brands', { ...listParams, purpose: 'options' }),
    queryFn: ({ signal }) => brandsApi.list(listParams, signal),
    staleTime: 5 * 60 * 1000,
  });

  const categoryOptions = (categories.data?.data ?? []).map((row) => ({
    value: row._id,
    label: row.name?.uz || row.slug || row._id,
  }));

  // Brend nomi oddiy satr — tarjima qilinmaydi
  const brandOptions = (brands.data?.data ?? []).map((row) => ({
    value: row._id,
    label: row.name || row.slug || row._id,
  }));

  const form = useContentForm<ProductForm>({
    collection: 'products',
    contentApi: productsApi,
    id,
    basePath: '/products',
    empty: () => ({
      title: emptyTranslatable(),
      slug: '',
      shortDescription: emptyTranslatable(),
      description: emptyTranslatable(),
      brandId: '',
      categoryIds: [],
      coverImage: null,
      gallery: [],
      video: null,
      specGroups: [],
      documents: [],
      variants: [],
      isFeatured: false,
      seo: emptySeo(),
    }),
    fromRecord: (record) => {
      const product = record as ProductRecord;
      return {
        title: product.title ?? emptyTranslatable(),
        slug: product.slug ?? '',
        shortDescription: product.shortDescription ?? emptyTranslatable(),
        description: product.description ?? emptyTranslatable(),
        brandId: product.brandId ?? '',
        categoryIds: product.categoryIds ?? [],
        coverImage: product.coverImage ?? null,
        gallery: product.gallery ?? [],
        video: product.video ?? null,
        specGroups: product.specGroups ?? [],
        documents: product.documents ?? [],
        variants: product.variants ?? [],
        isFeatured: product.isFeatured ?? false,
        seo: product.seo ?? emptySeo(),
      };
    },
    toInput: (value) => ({
      title: value.title,
      slug: value.slug,
      shortDescription: value.shortDescription,
      description: value.description,
      brandId: value.brandId || null,
      categoryIds: value.categoryIds,
      coverImage: value.coverImage,
      gallery: value.gallery,
      video: value.video,
      specGroups: value.specGroups.map((group, index) => ({ ...group, order: index })),
      documents: value.documents.map((doc, index) => ({ ...doc, order: index })),
      variants: value.variants.map((variant, index) => ({ ...variant, order: index })),
      isFeatured: value.isFeatured,
      seo: value.seo,
      // `priceMin`, `hasPrice`, `availabilityAgg` YUBORILMAYDI — ular
      // komplektatsiyalardan server tomonda hisoblanadi (docs/07-API.md §7).
    }),
  });

  if (form.loading) return <p className="p-6 text-sm text-n-600">Yuklanmoqda…</p>;
  if (form.notFound) return <p className="p-6 text-sm text-error-600">Mahsulot topilmadi.</p>;

  const counts: Partial<Record<Tab, number>> = {
    specs: form.form.specGroups.reduce((sum, group) => sum + group.rows.length, 0),
    variants: form.form.variants.length,
    media: form.form.gallery.length,
    documents: form.form.documents.length,
  };

  return (
    <ContentFormLayout
      title={form.form.title.uz || (form.isNew ? 'Yangi mahsulot' : '(nomsiz)')}
      backTo="/products"
      backLabel="Mahsulotlar"
      isNew={form.isNew}
      isPublished={form.isPublished}
      saving={form.saving}
      publishing={form.publishing}
      canPublish={user?.role !== 'editor'}
      autosave={form.autosave}
      lock={form.lock}
      unsaved={form.unsaved}
      onSave={form.save}
      onTogglePublish={form.togglePublish}
      onPreview={form.preview}
    >
      <div className="space-y-4 p-4 sm:p-6">
        {/*
          Yorliqlar maketda yuqori paneldan PASTDA, chiziq bilan
          belgilangan qatorda (`D3`, TAHRIRLASH): panelda ular
          «Saqlash» va «Nashr qilish» bilan bir joyda turib, qaysi
          biri harakat, qaysi biri navigatsiya ekani bilinmasdi.
        */}
        <Tabs
          items={TABS.map(([key, label]) => ({ key, label, count: counts[key] }))}
          value={tab}
          onChange={setTab}
          numbered
        />

        <DraftBanner
          savedAt={form.pendingDraft ? (form.record?.draftAt ?? null) : null}
          onRestore={form.restoreDraft}
          onDiscard={form.discardDraft}
        />

        {tab === 'main' && (
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="space-y-4 lg:col-span-2">
              <Card>
                <CardHeader title="Asosiy" />
                <CardBody className="space-y-4">
                  <TranslatableInput
                    label="Nomi"
                    required
                    value={form.form.title}
                    onChange={(title) => form.setForm((c) => ({ ...c, title }))}
                    error={form.fieldErrors['title.uz']}
                  />
                  <TranslatableInput
                    label="Qisqa tavsif"
                    multiline
                    rows={2}
                    value={form.form.shortDescription}
                    onChange={(shortDescription) =>
                      form.setForm((c) => ({ ...c, shortDescription }))
                    }
                    hint="Katalog kartochkasida ko‘rinadi"
                  />
                  <RichTextInput
                    label="To‘liq tavsif"
                    value={form.form.description}
                    onChange={(description) => form.setForm((c) => ({ ...c, description }))}
                  />
                </CardBody>
              </Card>
            </div>

            <Card>
              <CardHeader title="Katalogdagi o‘rni" />
              <CardBody className="space-y-4">
                <MediaPicker
                  label="Muqova rasmi"
                  value={form.form.coverImage}
                  onChange={(coverImage) => form.setForm((c) => ({ ...c, coverImage }))}
                />
                <Select
                  label="Brend"
                  value={form.form.brandId}
                  placeholder="Tanlanmagan"
                  options={brandOptions}
                  onChange={(event) => form.setForm((c) => ({ ...c, brandId: event.target.value }))}
                />
                <MultiSelect
                  label="Kategoriyalar"
                  max={10}
                  selected={form.form.categoryIds}
                  options={categoryOptions}
                  onChange={(categoryIds) => form.setForm((c) => ({ ...c, categoryIds }))}
                />
                <Input
                  label="Manzil (slug)"
                  value={form.form.slug}
                  onChange={(event) => form.setForm((c) => ({ ...c, slug: event.target.value }))}
                  error={form.fieldErrors.slug}
                />
                <Checkbox
                  label="Tanlangan mahsulot"
                  hint="Bosh sahifadagi «tanlanganlar» blokida chiqadi"
                  checked={form.form.isFeatured}
                  onChange={(event) =>
                    form.setForm((c) => ({ ...c, isFeatured: event.target.checked }))
                  }
                />
              </CardBody>
            </Card>
          </div>
        )}

        {tab === 'specs' && (
          <Card>
            <CardHeader
              title="Xarakteristikalar"
              description="Guruhlarga bo‘lingan jadval — mahsulot sahifasida va taqqoslashda ishlatiladi"
            />
            <CardBody>
              <SpecBuilder
                value={form.form.specGroups}
                currentProductId={form.isNew ? undefined : id}
                onChange={(specGroups) => form.setForm((c) => ({ ...c, specGroups }))}
              />
            </CardBody>
          </Card>
        )}

        {tab === 'variants' && (
          <Card>
            <CardHeader
              title="Komplektatsiyalar"
              description="Narx va mavjudlik shu yerda. Katalogdagi «dan» narxi eng arzonidan olinadi"
            />
            <CardBody>
              <VariantsEditor
                value={form.form.variants}
                onChange={(variants) => form.setForm((c) => ({ ...c, variants }))}
              />
            </CardBody>
          </Card>
        )}

        {tab === 'media' && (
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader title="Galereya" />
              <CardBody>
                <MediaGalleryPicker
                  label="Rasmlar"
                  value={form.form.gallery}
                  onChange={(gallery) => form.setForm((c) => ({ ...c, gallery }))}
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Video" />
              <CardBody className="space-y-4">
                <Checkbox
                  label="Video qo‘shilsin"
                  checked={form.form.video !== null}
                  onChange={(event) =>
                    form.setForm((c) => ({
                      ...c,
                      video: event.target.checked
                        ? { source: 'youtube', url: '', poster: null }
                        : null,
                    }))
                  }
                />

                {form.form.video && (
                  <>
                    <EnumSelect
                      label="Manba"
                      value={form.form.video.source}
                      options={[
                        { value: 'youtube', label: 'YouTube havolasi' },
                        { value: 'upload', label: 'Yuklangan fayl' },
                      ]}
                      onChange={(source) =>
                        form.setForm((c) => ({
                          ...c,
                          video: c.video ? { ...c.video, source } : null,
                        }))
                      }
                    />
                    <Input
                      label={
                        form.form.video.source === 'youtube' ? 'YouTube havolasi' : 'Fayl manzili'
                      }
                      value={form.form.video.url}
                      onChange={(event) =>
                        form.setForm((c) => ({
                          ...c,
                          video: c.video ? { ...c.video, url: event.target.value } : null,
                        }))
                      }
                    />
                    <MediaPicker
                      label="Muqova rasmi"
                      value={form.form.video.poster}
                      onChange={(poster) =>
                        form.setForm((c) => ({
                          ...c,
                          video: c.video ? { ...c.video, poster } : null,
                        }))
                      }
                    />
                  </>
                )}
              </CardBody>
            </Card>
          </div>
        )}

        {tab === 'documents' && (
          <Card>
            <CardHeader
              title="Hujjatlar"
              description="Qo‘llanma, sertifikat, texnik pasport — PDF ko‘rinishida"
            />
            <CardBody>
              <Repeater
                label="Fayllar"
                max={20}
                addLabel="Hujjat qo‘shish"
                items={form.form.documents}
                getId={(_, index) => `doc-${index}`}
                summary={(doc, index) => doc.title.uz || `${index + 1}-hujjat`}
                create={() =>
                  ({
                    title: emptyTranslatable(),
                    file: { mediaId: '', alt: null },
                    order: form.form.documents.length,
                  }) as ProductDocument
                }
                onChange={(documents) => form.setForm((c) => ({ ...c, documents }))}
                renderItem={(doc, update) => (
                  <>
                    <TranslatableInput
                      label="Nomi"
                      value={doc.title}
                      onChange={(title) => update({ ...doc, title })}
                    />
                    <MediaPicker
                      label="Fayl"
                      kind="file"
                      value={doc.file?.mediaId ? doc.file : null}
                      onChange={(file) =>
                        update({ ...doc, file: file ?? { mediaId: '', alt: null } })
                      }
                    />
                  </>
                )}
              />
            </CardBody>
          </Card>
        )}

        {tab === 'seo' && (
          <div className="max-w-2xl">
            <SeoPanel
              value={form.form.seo}
              onChange={(seo) => form.setForm((c) => ({ ...c, seo }))}
              fallbackTitle={form.form.title.uz || undefined}
            />
          </div>
        )}
      </div>
    </ContentFormLayout>
  );
}
